import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  XMarkIcon,
  ClipboardDocumentIcon,
  BoltIcon,
  StopIcon,
  LinkIcon,
  BanknotesIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { cn } from '../lib/utils';

const DEFAULT_SERVER_URL = (
  process.env.REACT_APP_UAZAPI_SERVER_URL || 'https://discadornet.uazapi.com'
).replace(/\/$/, '');

function formatBrl(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value) || 0);
}

function formatDateBr(iso) {
  if (!iso) return '—';
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString('pt-BR');
}

function invoiceStatusLabel(status) {
  const s = String(status || '').toUpperCase();
  if (['RECEIVED', 'CONFIRMED', 'RECEIVED_IN_CASH', 'DUNNING_RECEIVED'].includes(s)) return 'Paga';
  if (s === 'OVERDUE') return 'Em atraso';
  if (s === 'PENDING') return 'Pendente';
  return status || '—';
}

function statusLabel(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'connected') return 'Conectado';
  if (s === 'disconnected') return 'Desconectado';
  if (s === 'waiting_connection' || s === 'connecting') return 'Aguardando conexão';
  if (s === 'error') return 'Erro';
  return status || '—';
}

function statusBadgeVariant(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'connected') return 'default';
  if (s === 'error') return 'destructive';
  if (s === 'waiting_connection' || s === 'connecting') return 'secondary';
  return 'outline';
}

function DataRow({ label, value, mono, copyable }) {
  const copy = useCallback(() => {
    if (!value || !navigator.clipboard?.writeText) return;
    void navigator.clipboard.writeText(String(value)).then(
      () => toast.success(`${label} copiado.`),
      () => toast.error('Não foi possível copiar.')
    );
  }, [label, value]);

  return (
    <div className="space-y-0.5">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="flex items-start gap-2">
        <p className={cn('min-w-0 flex-1 break-all text-sm text-foreground', mono && 'font-mono text-[13px]')}>
          {value || '—'}
        </p>
        {copyable && value ? (
          <button
            type="button"
            onClick={copy}
            className="shrink-0 rounded p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground"
            title={`Copiar ${label}`}
            aria-label={`Copiar ${label}`}
          >
            <ClipboardDocumentIcon className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    </div>
  );
}

function InvoiceList({ title, items, emptyText, variant }) {
  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">{emptyText}</p>
      ) : (
        <ul className="max-h-36 space-y-2 overflow-y-auto pr-1">
          {items.map((inv) => (
            <li
              key={inv.id || `${inv.dueDate}-${inv.value}`}
              className="rounded-md border border-border/60 bg-background/50 px-3 py-2 text-xs"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium text-foreground">{formatBrl(inv.value)}</span>
                <Badge variant={variant} className="text-[10px]">
                  {invoiceStatusLabel(inv.status)}
                </Badge>
              </div>
              <p className="mt-1 text-muted-foreground">Venc.: {formatDateBr(inv.dueDate)}</p>
              {inv.paymentDate ? (
                <p className="text-muted-foreground">Pago em: {formatDateBr(inv.paymentDate)}</p>
              ) : null}
              {inv.invoiceUrl ? (
                <a
                  href={inv.invoiceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 inline-block text-primary underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  Abrir fatura
                </a>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function WhatsAppInstanceDetailModal({
  open,
  instance,
  serverUrl = DEFAULT_SERVER_URL,
  apiBaseUrl,
  onClose,
  onConnect,
  onDisconnect,
  onConfigureWebhook,
  onVerifyPayment,
  actionLoading = false,
  showToken = true
}) {
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);

  const loadBilling = useCallback(async () => {
    if (!open || !instance || !apiBaseUrl) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    const headers = { Authorization: `Bearer ${token}` };
    let url = null;
    if (instance._id) {
      url = `${apiBaseUrl.replace(/\/$/, '')}/api/whatsapp-instances/${instance._id}/billing`;
    } else if (instance.uazapiToken) {
      url = `${apiBaseUrl.replace(/\/$/, '')}/api/whatsapp-instances/uazapi-token/billing?token=${encodeURIComponent(instance.uazapiToken)}`;
    }
    if (!url) return;

    setBillingLoading(true);
    try {
      const res = await axios.get(url, { headers });
      setBilling(res.data);
    } catch (e) {
      setBilling(null);
      toast.error(e.response?.data?.message || 'Erro ao carregar faturas');
    } finally {
      setBillingLoading(false);
    }
  }, [open, instance, apiBaseUrl]);

  useEffect(() => {
    if (open && instance) {
      void loadBilling();
    } else {
      setBilling(null);
    }
  }, [open, instance, loadBilling]);

  const handleVerifyPayment = async () => {
    if (!billing?.orderId || !onVerifyPayment) return;
    setVerifyLoading(true);
    try {
      await onVerifyPayment(billing.orderId);
      await loadBilling();
    } finally {
      setVerifyLoading(false);
    }
  };

  if (!open || !instance) return null;

  const title =
    instance.uazapiInstanceName ||
    instance.name ||
    instance.uazapiInstanceId ||
    'Instância';
  const status = instance.status || instance.uazapiStatus;
  const phone = instance.phoneNumber || instance.owner;
  const canManageWebhook = Boolean(instance._id || instance.uazapiToken);
  const canConnect = status !== 'connected' && canManageWebhook;
  const canDisconnect =
    (status === 'connected' || status === 'waiting_connection') && canManageWebhook;

  const showBilling = Boolean(apiBaseUrl && (instance._id || instance.uazapiToken));
  const priceLabel =
    billing?.price != null
      ? `${formatBrl(billing.price)}/${billing.billingCycleLabel || 'mês'}`
      : null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-background/85 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="wa-detail-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="relative max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-xl border border-border bg-card text-card-foreground shadow-xl">
        <button
          type="button"
          className="absolute right-4 top-4 z-10 rounded-md p-2 text-muted-foreground transition hover:bg-muted hover:text-foreground"
          onClick={() => onClose?.()}
          aria-label="Fechar"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        <div className="border-b border-border px-6 py-5 pr-14">
          <h2 id="wa-detail-title" className="font-mono text-lg font-semibold tracking-tight text-foreground">
            {title}
          </h2>
          {instance.name && instance.name !== title ? (
            <p className="mt-1 text-sm text-muted-foreground">{instance.name}</p>
          ) : null}
        </div>

        <div className="grid gap-6 p-6 md:grid-cols-[1fr_auto]">
          <div className="space-y-6">
            <div className="rounded-lg border border-dashed border-primary/50 bg-muted/20 p-5">
              <h3 className="mb-4 text-sm font-semibold text-foreground">Dados da instância</h3>
              <div className="space-y-4">
                <DataRow label="Server URL" value={serverUrl} mono copyable />
                {showToken ? (
                  <DataRow label="Instance Token" value={instance.uazapiToken} mono copyable />
                ) : null}
                <DataRow label="Número conectado" value={phone} mono />
                <div className="space-y-0.5">
                  <p className="text-xs text-muted-foreground">Status</p>
                  <Badge variant={statusBadgeVariant(status)} className="text-sm font-semibold">
                    {statusLabel(status)}
                  </Badge>
                </div>
                {instance.uazapiInstanceId ? (
                  <DataRow label="ID UAZAPI" value={instance.uazapiInstanceId} mono copyable />
                ) : null}
                {instance.systemName ? <DataRow label="System name" value={instance.systemName} /> : null}
                {instance.userNome || instance.userEmail ? (
                  <DataRow
                    label="Cliente"
                    value={[instance.userNome, instance.userEmail].filter(Boolean).join(' · ')}
                  />
                ) : null}
              </div>
            </div>

            {showBilling ? (
              <div className="rounded-lg border border-border bg-muted/10 p-5">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <BanknotesIcon className="h-4 w-4 text-primary" />
                    Financeiro
                    {priceLabel ? (
                      <span className="font-normal text-muted-foreground">({priceLabel})</span>
                    ) : null}
                  </h3>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8"
                    disabled={billingLoading}
                    onClick={() => void loadBilling()}
                  >
                    <ArrowPathIcon className={cn('h-4 w-4', billingLoading && 'animate-spin')} />
                  </Button>
                </div>

                {billingLoading ? (
                  <p className="text-sm text-muted-foreground">Carregando faturas…</p>
                ) : billing?.exempt ? (
                  <p className="text-sm text-muted-foreground">
                    {billing.warningMessage || 'Sem cobrança recorrente para esta instância.'}
                  </p>
                ) : (
                  <div className="space-y-4">
                    {billing?.unavailableUntilPaid && billing?.warningMessage ? (
                      <div
                        className="flex gap-3 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-foreground"
                        role="alert"
                      >
                        <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-destructive" />
                        <div>
                          <p className="font-medium text-destructive">Pagamento pendente</p>
                          <p className="mt-1 text-muted-foreground">{billing.warningMessage}</p>
                          <p className="mt-2 text-xs text-muted-foreground">
                            Até a confirmação do pagamento, o uso da instância pode ficar indisponível.
                          </p>
                        </div>
                      </div>
                    ) : null}

                    {billing?.pendingPayment ? (
                      <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3">
                        <p className="text-xs font-medium uppercase text-amber-700 dark:text-amber-400">
                          Fatura em aberto para pagamento
                        </p>
                        <p className="mt-1 text-sm font-semibold text-foreground">
                          {formatBrl(billing.pendingPayment.value)}
                          {billing.pendingPayment.dueDate
                            ? ` · vence em ${formatDateBr(billing.pendingPayment.dueDate)}`
                            : ''}
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {billing.pendingPayment.invoiceUrl ? (
                            <a
                              href={billing.pendingPayment.invoiceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                            >
                              Pagar agora
                            </a>
                          ) : null}
                          {billing.orderId && onVerifyPayment ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={verifyLoading}
                              onClick={handleVerifyPayment}
                            >
                              {verifyLoading ? 'Verificando…' : 'Já paguei — verificar'}
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    ) : null}

                    <div className="grid gap-4 sm:grid-cols-3">
                      <InvoiceList
                        title="A pagar"
                        items={billing?.invoices?.due || []}
                        emptyText="Nenhuma fatura em atraso."
                        variant="destructive"
                      />
                      <InvoiceList
                        title="Abertas"
                        items={billing?.invoices?.open || []}
                        emptyText="Nenhuma fatura aberta."
                        variant="secondary"
                      />
                      <InvoiceList
                        title="Pagas"
                        items={billing?.invoices?.paid || []}
                        emptyText="Nenhuma fatura paga ainda."
                        variant="outline"
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>

          <div className="flex flex-col items-center gap-4 md:min-w-[220px]">
            <div className="h-36 w-36 overflow-hidden rounded-lg border border-border bg-muted">
              {instance.profilePicUrl ? (
                <img src={instance.profilePicUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
                  Sem foto
                </div>
              )}
            </div>
            <div className="flex w-full flex-col gap-2">
              {canManageWebhook ? (
                <Button
                  type="button"
                  className="w-full"
                  disabled={actionLoading}
                  onClick={() => onConfigureWebhook?.(instance)}
                >
                  <LinkIcon className="mr-2 h-4 w-4" />
                  Configurar Webhook
                </Button>
              ) : null}
              {canConnect ? (
                <Button
                  type="button"
                  className="w-full"
                  disabled={actionLoading}
                  onClick={() => onConnect?.(instance)}
                >
                  <BoltIcon className="mr-2 h-4 w-4" />
                  Conectar
                </Button>
              ) : null}
              {canDisconnect ? (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-destructive/40 text-destructive hover:bg-destructive/10"
                  disabled={actionLoading}
                  onClick={() => onDisconnect?.(instance)}
                >
                  <StopIcon className="mr-2 h-4 w-4" />
                  Desconectar
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
